package org.example.project.enginevertrieb

import androidx.compose.runtime.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.example.project.settings.*
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.Node
import org.w3c.dom.events.Event

/** Shared editable contact column for both sales flows. */
@Composable
internal fun FlowContactDetails(lead: All, headers: List<VertriebsindexApi.ZusatzHeader>, enabled: Boolean,
    isLinkedIn: Boolean = false,
    onSave: suspend (All, String) -> Unit) {
    val scope = rememberCoroutineScope()
    var saving by remember(lead.id) { mutableStateOf(false) }
    var error by remember(lead.id) { mutableStateOf("") }
    var addressEditing by remember(lead.id) { mutableStateOf(false) }
    var street by remember(lead.id) { mutableStateOf(lead.strasse) }
    var houseNumber by remember(lead.id) { mutableStateOf(lead.hausnummer) }
    var postalCode by remember(lead.id) { mutableStateOf(lead.plz) }
    var city by remember(lead.id) { mutableStateOf(lead.ort) }
    val currentLead by rememberUpdatedState(lead)
    fun persist(value: All, field: String, onSaved: () -> Unit = {}) {
        if (!enabled || saving) return
        saving = true; error = ""
        scope.launch {
            try { onSave(value, field); onSaved() }
            catch (e: CancellationException) { throw e }
            catch (e: Exception) { error = "$field: ${e.message ?: "Speichern fehlgeschlagen."}" }
            finally { saving = false }
        }
    }
    fun editAddress() {
        if (!enabled || saving || addressEditing) return
        street = lead.strasse; houseNumber = lead.hausnummer; postalCode = lead.plz; city = lead.ort
        addressEditing = true
    }
    fun commitAddress() {
        if (!addressEditing || saving) return
        val updated = currentLead.copy(strasse = street.trim(), hausnummer = houseNumber.trim(), plz = postalCode.trim(), ort = city.trim())
        if (listOf(updated.strasse, updated.hausnummer, updated.plz, updated.ort) ==
            listOf(currentLead.strasse, currentLead.hausnummer, currentLead.plz, currentLead.ort)) addressEditing = false
        else persist(updated, "Adresse") { addressEditing = false }
    }
    @Composable fun row(label: String, icon: String, onClick: (() -> Unit)? = null, content: @Composable () -> Unit) {
        Div({
            if (onClick != null) onClick { onClick() }
            style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px); padding(7.px, 0.px); minWidth(0.px) }
        }) {
            Img(src = "images/$icon.svg", attrs = { attr("alt", ""); style { width(21.px); height(21.px); flexShrink(0); property("filter", "brightness(0) invert(1)") } })
            Span({ style { width(115.px); flexShrink(0); fontSize(13.px) } }) { Text("$label:") }
            Div({ style { flex("1"); minWidth(0.px) } }) { content() }
        }
    }
    @Composable fun field(label: String, icon: String, value: String, change: (All, String) -> All) {
        key(lead.id, label) {
            var text by remember(value) { mutableStateOf(value) }
            row(label, icon) {
                Input(InputType.Text, attrs = {
                    value(text); attr("aria-label", label)
                    if (!enabled || saving) disabled()
                    onInput { text = it.value }
                    onBlur { if (text != value) persist(change(lead, text), label) }
                    onKeyDown { if (it.key == "Enter") it.target.asDynamic().blur() }
                    style { width(100.percent); minWidth(0.px); height(34.px); boxSizing("border-box"); padding(4.px, 6.px); color(Color.white); backgroundColor(Color.transparent); property("border", "none"); property("border-bottom", "1px solid rgba(255,255,255,.18)") }
                })
            }
        }
    }
    Div({ style { paddingTop(12.px); minWidth(0.px) } }) {
        row("ID", "identity") { Text(lead.id) }
        if (isLinkedIn) row("Netzwerkstatus", "snap") { Text(lead.linkedinNetzwerkstatus) }
        if (!isLinkedIn) row("ICP", "filter") { Text(lead.icp) }
        row("Lead Typ", "filter") { Text(lead.leadTyp) }
        row("Zustand", "info") {
            Text(if (lead.id.isNotBlank()) checkpointLabel(lead.vertriebCheckpoint.ifBlank { "MY_LEADS" }) else "")
        }
        field("Firma", "unternehmen", lead.firma) { v, t -> v.copy(firma = t) }
        row("Adresse", "adresse", onClick = { editAddress() }) {
            if (addressEditing) Div({
                ref { element ->
                    val focusOut: (Event) -> Unit = { event ->
                        val next = event.asDynamic().relatedTarget as? Node
                        if (next == null || !element.contains(next)) commitAddress()
                    }
                    element.addEventListener("focusout", focusOut)
                    onDispose { element.removeEventListener("focusout", focusOut) }
                }
                style { display(DisplayStyle.Grid); gridTemplateColumns("repeat(2, minmax(0, 1fr))"); gap(12.px) }
            }) {
                @Composable fun addressField(label: String, value: String, first: Boolean = false, change: (String) -> Unit) {
                    Label(attrs = { style { minWidth(0.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(4.px); fontSize(11.px) } }) {
                        Text(label)
                        Input(InputType.Text, attrs = {
                            value(value); attr("aria-label", label)
                            if (!enabled || saving) disabled()
                            if (label == "PLZ") attr("inputmode", "numeric")
                            if (first) ref { element -> element.focus(); onDispose {} }
                            onInput { change(it.value) }
                            onKeyDown {
                                if (it.key == "Enter") { it.preventDefault(); commitAddress() }
                                if (it.key == "Escape") { it.preventDefault(); addressEditing = false }
                            }
                            style {
                                width(100.percent); minWidth(0.px); height(34.px); padding(4.px, 6.px); boxSizing("border-box")
                                color(Color.white); backgroundColor(Color.transparent); border(0.px); property("border-bottom", "1px solid rgba(255,255,255,.18)")
                            }
                        })
                    }
                }
                addressField("Straße", street, first = true) { street = it }
                addressField("Hausnummer", houseNumber) { houseNumber = it }
                addressField("PLZ", postalCode) { postalCode = it }
                addressField("Ort", city) { city = it }
            } else Button(attrs = {
                attr("type", "button"); attr("aria-label", "Adresse bearbeiten")
                if (!enabled || saving) disabled()
                onClick { editAddress() }
                style { width(100.percent); minHeight(34.px); padding(4.px, 6.px); border(0.px); backgroundColor(Color.transparent); color(Color.white); textAlign("left"); cursor(if (enabled && !saving) "text" else "default"); property("overflow-wrap", "anywhere") }
            }) {
                val line1 = listOf(lead.strasse, lead.hausnummer).map { it.trim() }.filter { it.isNotBlank() }.joinToString(" ")
                val line2 = listOf(lead.plz, lead.ort).map { it.trim() }.filter { it.isNotBlank() }.joinToString(" ")
                Text(listOf(line1, line2).filter { it.isNotBlank() }.joinToString(", "))
            }
        }
        row("Google Maps", "googlemaps") {
            val url = vertriebGoogleMapsUrl(lead)
            if (url.isNotBlank()) A(href = url, attrs = { attr("target", "_blank"); attr("rel", "noopener noreferrer"); style { color(Color.white); property("overflow-wrap", "anywhere") } }) { Text("Adresse öffnen") }
        }
        field("Webseite", "webseite", lead.webseite) { v, t -> v.copy(webseite = t) }
        field("Ansprechpartner", "person", lead.ansprechpartner) { v, t -> v.copy(ansprechpartner = t) }
        field("Position", "role", lead.ansprechpartnerPosition) { v, t -> v.copy(ansprechpartnerPosition = t) }
        field("E-Mail", "mail", lead.email) { v, t -> v.copy(email = t) }
        if (isLinkedIn && lead.leadQuelle != "Web") row("LinkedIn", "linkedin") {
            if (lead.linkedin.isNotBlank()) A(href = lead.linkedin, attrs = { attr("target", "_blank"); attr("rel", "noopener noreferrer"); style { color(Color.white); property("overflow-wrap", "anywhere") } }) { Text(lead.linkedin) }
        } else field("LinkedIn", "linkedin", lead.linkedin) { v, t -> v.copy(linkedin = t) }
        field("Telefon", "call", lead.telefon) { v, t -> v.copy(telefon = t) }
        field("Mobil", "call", lead.mobil) { v, t -> v.copy(mobil = t) }
        if (isLinkedIn) row("Vernetzt seit", "info") { Text(linkedInConnectedDate(lead.linkedinVernetztAm)) }
        val excludedLinkedInFields = setOf("hallen", "tore", "regale", "regalplaetze", "anzahlhallen", "anzahltore", "anzahlregale", "anzahlregalplaetze")
        fun fieldKey(value: String) = value.lowercase().replace("ä", "ae").filter { it.isLetterOrDigit() }
        headers.filterNot { isLinkedIn &&
            (fieldKey(it.label) in excludedLinkedInFields || fieldKey(it.id) in excludedLinkedInFields) }.forEach { header ->
            val value = lead.vertriebZusatzfelder[header.id].orEmpty()
            val icon = when (header.label) { "Hallen" -> "lager"; "Tore" -> "toruebersicht"; "Regalplätze" -> "regaluebersicht"; else -> "info" }
            if (header.typ == "CHECKBOX") row(header.label, icon) {
                LumaticaCheckbox(checked = value in listOf("true", "1", "ja", "✅"), enabled = enabled && !saving,
                    onChange = { persist(lead.copy(vertriebZusatzfelder = lead.vertriebZusatzfelder + (header.id to if (it) "true" else "false")), header.label) })
            } else field(header.label, icon, value) { v, t -> v.copy(vertriebZusatzfelder = v.vertriebZusatzfelder + (header.id to t)) }
        }
        if (saving) Div({ attr("role", "status") }) { Text("Speichert …") }
        if (error.isNotBlank()) Div({ attr("role", "alert"); style { color(Color("#ffb4b4")); padding(8.px) } }) { Text(error) }
    }
}

internal suspend fun saveFlowContact(value: All): All {
    return VertriebsindexApi.saveTabellenEintrag(value) ?: error("Speichern wurde nicht bestätigt.")
}
